import { useMemo, useState, type ReactNode } from 'react';
import type { Map as MlMap } from 'maplibre-gl';
import { Button, DriverCard, Handle, MapSheet, SheetFoot } from '../components/ui';
import { MapLine, useFit, useRouteGlide } from '../components/MapView';
import { clock, km } from '../lib/format';
import { roadEstimate } from '../lib/geo';
import { routeLine, splitAt } from '../lib/polyline';
import { nearestLandmark, shortName } from '../lib/places';
import type { Ride, ShareLink } from '../lib/types';
import { words } from '../lib/vehicles';
import { useT } from '../lib/i18n';
import { useColours } from '../theme/usePalette';
import { DropSquare, MapPill, MapStrip, StartRing, VehicleDisc } from './mapBits';
import { SosSheet } from './Safety';

export function OnTrip({ ride, link, onShare, onStopShare, contactNames, shareNote, map }: { ride: Ride; link: (ShareLink & { auto?: boolean }) | null; onShare: () => void; onStopShare: () => void; contactNames: string[]; shareNote: ReactNode; map: MlMap | null }) {
  const pal = useColours('map');
  const t = useT();
  const [sos, setSos] = useState(false);
  const d = ride.driver;
  const loc = d?.location ?? ride.pickup;
  // the road line (Ola) or the drawn curve; the vehicle moves along it and the line splits where it is
  const route = useMemo(() => routeLine(ride.pickup, ride.drop, ride.routePolyline, 0.12, 48), [ride.pickup, ride.drop, ride.routePolyline]);
  const shown = useRouteGlide(loc, route, 1600);
  const left = roadEstimate(loc, ride.drop);
  const mins = Math.max(1, Math.round(left.seconds / 60));
  const arrives = clock(new Date(Date.now() + left.seconds * 1000));
  const passing = nearestLandmark(loc, 1200, ['place']);
  const { done, rest } = splitAt(route, shown);
  const vehicle = d?.vehicle ? `${d.vehicle.make} ${d.vehicle.model} · ${d.vehicle.colour.toLowerCase()}` : words(t, ride.vehicleType).label;

  // the whole trip between the ETA card and the sheet; the route's drop pill sits left of its point
  useFit(map, { points: [ride.pickup, loc, ride.drop], padding: { top: 150, bottom: 70, left: 150, right: 60 }, maxZoom: 16 });

  return (
    <>
      <MapLine id="done" coords={done} color={pal.routeDone} width={3.8} dash={[0.1, 2.2]} />
      <MapLine id="rest" coords={rest} color={pal.route} width={4.1} casing={pal.routeCase} casingWidth={8.3} />
      <StartRing at={ride.pickup} color="var(--routeDone)" size={14} stroke={3} />
      <DropSquare at={ride.drop} size={14} />
      <MapPill at={ride.drop} side="left">
        {shortName(ride.drop.address)} <span style={{ fontWeight: 500, color: 'var(--ink2)' }}>{arrives}</span>
      </MapPill>
      <VehicleDisc at={loc} type={ride.vehicleType} size={44} halo route={route} heading={d?.location?.heading} />

      <div className="zr-fadein" style={{ position: 'absolute', left: 'calc(var(--map-l) + 20px)', top: 'calc(var(--sat) + 10px)', borderRadius: 20, background: 'var(--float)', boxShadow: 'var(--shadow)', padding: '10px 18px 12px 16px', zIndex: 5 }} aria-live="polite">
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
          <span key={mins} className="zr-num zr-tick" style={{ fontSize: 36, lineHeight: '40px', fontWeight: 900, letterSpacing: '-0.04em' }}>{mins}</span>
          <span style={{ fontSize: 16, fontWeight: 700 }}>{t('common.min')}</span>
        </div>
        <div className="zr-num" style={{ fontSize: 13, lineHeight: '18px', fontWeight: 500, color: 'var(--ink2)' }}>
          {t('trip.arrives', { time: arrives, left: km(left.meters) })}
        </div>
      </div>
      <button
        type="button"
        aria-label={t('trip.sos')}
        onClick={() => setSos(true)}
        style={{ position: 'absolute', right: 20, top: 'calc(var(--sat) + 14px)', width: 60, height: 60, border: 0, borderRadius: 30, background: 'var(--danger)', color: '#FFFFFF', boxShadow: '0 0 0 4px var(--sosRing), 0 8px 20px rgba(217,45,32,0.35)', fontSize: 15, fontWeight: 900, letterSpacing: '0.04em', cursor: 'pointer', zIndex: 5 }}
      >
        SOS
      </button>

      <MapSheet label={t('trip.details')} art={<MapStrip />}>
        <div className="zr-swap" style={{ display: 'flex', flexDirection: 'column' }}>
          <Handle />
          <DriverCard boxed={false} size={44} style={{ marginTop: 16 }} name={d?.firstName ?? t('common.yourDriver')} line={vehicle} plate={d?.vehicle?.plate} />

          <div className="zr-card" style={{ marginTop: 14, borderRadius: 18, padding: '10px 6px 10px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <span aria-hidden="true" style={{ flex: 'none', width: 10, height: 10, borderRadius: 5, background: link ? 'var(--ac)' : 'var(--ink3)', boxShadow: '0 0 0 4px var(--acSoft)', animation: link ? 'zr-breathe 2.4s ease-in-out infinite' : undefined }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div key={link ? 'on' : 'off'} className="zr-ellipsis zr-tick" style={{ fontSize: 14, lineHeight: '19px', fontWeight: 700 }}>
                {link ? (contactNames.length ? t('trip.linkFor', { names: contactNames.join(', ') }) : t('trip.linkOn')) : t('trip.shareLive')}
              </div>
              <div style={{ fontSize: 12.5, lineHeight: '17px', fontWeight: 500, color: 'var(--ink2)' }}>
                {link ? (link.auto ? t('trip.auto') : t('trip.anyone')) : t('trip.send')}
              </div>
            </div>
            <button type="button" onClick={link ? onStopShare : onShare} style={{ flex: 'none', height: 44, padding: '0 12px', border: 0, background: 'transparent', color: 'var(--acText)', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              {link ? t('trip.stop') : t('trip.share')}
            </button>
          </div>
          {shareNote}
        <ol aria-label={t('trip.journey')} style={{ margin: '14px 0 0', padding: 0, listStyle: 'none', position: 'relative' }}>
          <span aria-hidden="true" style={{ position: 'absolute', left: 9, top: 20, width: 2, height: 80, background: 'var(--s3)' }} />
          <li style={{ position: 'relative', height: 40, display: 'flex', alignItems: 'center', gap: 14 }}>
            <span aria-hidden="true" style={{ flex: 'none', width: 20, display: 'flex', justifyContent: 'center' }}>
              <span style={{ width: 10, height: 10, borderRadius: 5, background: 'var(--ink3)' }} />
            </span>
            <span style={{ flex: 1, fontSize: 14, fontWeight: 500, color: 'var(--ink2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t('trip.picked', { place: shortName(ride.pickup.address) })}</span>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink3)', fontVariantNumeric: 'tabular-nums' }}>{ride.startedAt ? clock(ride.startedAt) : ''}</span>
          </li>
          <li style={{ position: 'relative', height: 40, display: 'flex', alignItems: 'center', gap: 14 }}>
            <span aria-hidden="true" style={{ flex: 'none', width: 20, display: 'flex', justifyContent: 'center' }}>
              <span style={{ width: 14, height: 14, borderRadius: 7, boxSizing: 'border-box', border: '3px solid var(--acText)', background: 'var(--sheet)' }} />
            </span>
            <span key={passing?.name ?? 'way'} className="zr-ellipsis zr-tick" style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 700 }}>{passing ? t('trip.passing', { place: passing.name }) : t('trip.onWay')}</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--acText)' }}>{t('common.now')}</span>
          </li>
          <li style={{ position: 'relative', height: 40, display: 'flex', alignItems: 'center', gap: 14 }}>
            <span aria-hidden="true" style={{ flex: 'none', width: 20, display: 'flex', justifyContent: 'center' }}>
              <span style={{ width: 11, height: 11, borderRadius: 2.5, background: 'var(--ink)' }} />
            </span>
            <span style={{ flex: 1, fontSize: 14, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t('trip.drop', { place: shortName(ride.drop.address) })}</span>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink2)', fontVariantNumeric: 'tabular-nums' }}>{arrives}</span>
          </li>
        </ol>

        </div>

        {/* calling the driver needs a masked-calling provider: not shown until there is one */}
        <SheetFoot style={{ marginTop: 'auto', paddingTop: 'calc(24px * var(--vg))' }}>
          <Button variant="secondary" icon="share" onClick={onShare}>
            {t('trip.shareTrip')}
          </Button>
        </SheetFoot>
      </MapSheet>
      <SosSheet open={sos} onClose={() => setSos(false)} ride={ride} />
    </>
  );
}
